import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileDown, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";

/**
 * PengeluaranProyekTab — 2026-06-21 (user request): 2 sub-tab.
 * 1. SUMBER KAS — pengeluaran dikelompokkan per sumber (Petty Cash per
 *    minggu·staf; Buku Bank per mutasi) + status alokasi per transaksi.
 * 2. VERSI RAB — hierarki RAB (seperti Rincian) + nilai RAB, pengeluaran
 *    teralokasi (nilai ALOKASI, bukan nominal transaksi), sisa anggaran,
 *    % terpakai; item bisa di-expand untuk melihat transaksi pembentuknya.
 * Keduanya agregasi read-only dari data existing (tidak mengubah data sumber).
 */

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

const STATUS_STYLE = {
  penuh: { label: "Sudah Dialokasikan", cls: "bg-emerald-100 text-emerald-800" },
  sebagian: { label: "Teralokasi Sebagian", cls: "bg-amber-100 text-amber-800" },
  belum: { label: "Belum Diklasifikasikan", cls: "bg-rose-100 text-rose-700" },
};
const statusOf = (s) => {
  const rem = Number(s.remaining || 0);
  if (rem <= 0.01) return "penuh";
  if (Number(s.allocated_total || 0) > 0.01) return "sebagian";
  return "belum";
};
const StatusBadge = ({ k, testid }) => (
  <span className={`inline-block px-1.5 py-px text-[9px] uppercase tracking-wider font-bold ${STATUS_STYLE[k].cls}`} data-testid={testid}>
    {STATUS_STYLE[k].label}
  </span>
);

export default function PengeluaranProyekTab({ pid }) {
  const [sub, setSub] = useState("sumber"); // "sumber" | "rab"

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/active-projects/${pid}/cost-control/pengeluaran/export.${fmt}?view=${sub}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = sub === "rab" ? `pengeluaran_versi_rab.${fmt}` : `pengeluaran_sumber_kas.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Pengeluaran proyek berhasil diekspor (${fmt.toUpperCase()})`);
    } catch { toast.error("Gagal mengekspor pengeluaran proyek."); }
  };

  return (
    <div className="space-y-3" data-testid="cc-exp2-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex gap-1 border-b border-slate-200" data-testid="cc-exp2-subtabs">
          {[["sumber", "Sumber Kas"], ["rab", "Versi RAB"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSub(k)} data-testid={`cc-exp2-subtab-${k}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px transition-colors ${sub === k ? "border-[#0B1D3A] text-[#0B1D3A]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button type="button" onClick={() => doExport("xlsx")} data-testid="cc-exp2-export-xlsx"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <FileDown size={13} /> Export Excel
          </button>
          <button type="button" onClick={() => doExport("pdf")} data-testid="cc-exp2-export-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <FileText size={13} /> Export PDF
          </button>
        </div>
      </div>
      {sub === "sumber" ? <SumberKasView pid={pid} /> : <VersiRabView pid={pid} />}
    </div>
  );
}

/* ============================== SUB-TAB 1: SUMBER KAS ============================== */

function SumberKasView({ pid }) {
  const [sources, setSources] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const s = await api.get(`/active-projects/${pid}/cost-control/expense-sources`).then((r) => r.data?.sources || []);
      setSources(s);
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat data sumber kas.");
    }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const bank = useMemo(() => (sources || []).filter((s) => s.source_type !== "petty_cash"), [sources]);
  const pcGroups = useMemo(() => {
    const map = new Map();
    for (const s of (sources || []).filter((x) => x.source_type === "petty_cash")) {
      const key = `${s.week_label || "—"} · ${s.pc_staff || "—"}`;
      if (!map.has(key)) map.set(key, { key, items: [], subtotal: 0 });
      const g = map.get(key);
      g.items.push(s);
      g.subtotal += Number(s.amount || 0);
    }
    return [...map.values()];
  }, [sources]);
  const bankTotal = useMemo(() => bank.reduce((a, s) => a + Number(s.amount || 0), 0), [bank]);
  const pcTotal = useMemo(() => pcGroups.reduce((a, g) => a + g.subtotal, 0), [pcGroups]);

  if (err) return <div className="p-4 text-sm text-rose-700" data-testid="cc-exp2-error">{err}</div>;
  if (sources === null) return <div className="p-6 text-sm text-slate-500" data-testid="cc-exp2-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat data sumber kas…</div>;

  return (
    <div className="space-y-3" data-testid="cc-exp2-sumber">
      <p className="text-[11px] text-slate-500" data-testid="cc-exp2-sumber-hint">
        Seluruh pengeluaran proyek dikelompokkan berdasarkan sumbernya: mutasi <span className="font-semibold text-slate-700">Buku Bank</span> (Pembayaran) dan item <span className="font-semibold text-slate-700">Petty Cash</span> terverifikasi FAT per periode minggu · staf. Status menunjukkan apakah transaksi sudah dialokasikan ke item RAB di tab Klasifikasi Pengeluaran.
      </p>

      {/* BUKU BANK */}
      <div className="border border-slate-200 overflow-hidden bg-white" data-testid="cc-exp2-bank-section">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A] flex items-center justify-between">
          <span>Buku Bank (Mutasi Pembayaran)</span>
          <span className="tabular-nums" data-testid="cc-exp2-bank-total">{formatIDR(bankTotal)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="text-left bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-1.5 overline text-slate-700 w-44">Rekening</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-24" title="Tanggal terjadinya transaksi pada sumber data — bukan tanggal input, pelaporan, verifikasi, atau alokasi.">Tanggal Transaksi</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-36">Referensi</th>
                <th className="px-2 py-1.5 overline text-slate-700">Deskripsi</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-28 text-right">Nominal</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-36">Status</th>
              </tr>
            </thead>
            <tbody data-testid="cc-exp2-bank-tbody">
              {bank.map((s) => (
                <tr key={s.movement_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`cc-exp2-bank-row-${s.movement_id}`}>
                  <td className="px-2 py-1.5 text-slate-600">{s.account_bank_name || "—"}{s.account_no ? ` · ${s.account_no}` : ""}</td>
                  <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{fmtDate(s.date)}</td>
                  <td className="px-2 py-1.5 text-slate-500 break-words">{s.doc_no || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-800 break-words">{s.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-700">{formatIDR(s.amount)}</td>
                  <td className="px-2 py-1.5"><StatusBadge k={statusOf(s)} testid={`cc-exp2-bank-status-${s.movement_id}`} /></td>
                </tr>
              ))}
              {bank.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-5 text-center text-[11px] text-slate-400 italic" data-testid="cc-exp2-bank-empty">
                  Belum ada mutasi pembayaran Buku Bank untuk proyek ini.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PETTY CASH */}
      <div className="border border-slate-200 overflow-hidden bg-white" data-testid="cc-exp2-pc-section">
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[10px] uppercase tracking-wider font-bold text-amber-800 flex items-center justify-between">
          <span>Petty Cash (Terverifikasi FAT)</span>
          <span className="tabular-nums" data-testid="cc-exp2-pc-total">{formatIDR(pcTotal)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="text-left bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-1.5 overline text-slate-700 w-24" title="Tanggal aktual pengeluaran item petty cash — bukan tanggal pengajuan, transfer, atau verifikasi.">Tanggal Transaksi</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-32">Voucher</th>
                <th className="px-2 py-1.5 overline text-slate-700">Deskripsi</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-44">COA</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-28 text-right">Nominal</th>
                <th className="px-2 py-1.5 overline text-slate-700 w-36">Status</th>
              </tr>
            </thead>
            <tbody data-testid="cc-exp2-pc-tbody">
              {pcGroups.map((g) => (
                <Fragment key={g.key}>
                  <tr className="bg-amber-50/60 border-b border-amber-100" data-testid={`cc-exp2-pc-group-${g.key}`}>
                    <td colSpan={4} className="px-2 py-1.5 font-bold text-[#0B1D3A]">{g.key}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-700">{formatIDR(g.subtotal)}</td>
                    <td></td>
                  </tr>
                  {g.items.map((s) => (
                    <tr key={s.movement_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`cc-exp2-pc-row-${s.movement_id}`}>
                      <td className="px-2 py-1 text-slate-600 whitespace-nowrap pl-6">{s.date ? fmtDate(s.date) : <span className="text-amber-700 italic text-[10px]">Tanggal transaksi belum tersedia</span>}</td>
                      <td className="px-2 py-1 text-slate-500 break-words">{s.doc_no || "—"}</td>
                      <td className="px-2 py-1 text-slate-800 break-words">{s.description || "—"}</td>
                      <td className="px-2 py-1 text-slate-600">{s.coa_code ? `${s.coa_code} — ${s.coa_name || ""}` : "—"}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-600">{formatIDR(s.amount)}</td>
                      <td className="px-2 py-1"><StatusBadge k={statusOf(s)} testid={`cc-exp2-pc-status-${s.movement_id}`} /></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {pcGroups.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-5 text-center text-[11px] text-slate-400 italic" data-testid="cc-exp2-pc-empty">
                  Belum ada pengeluaran petty cash terverifikasi untuk proyek ini.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-[#0B1D3A] bg-[#0B1D3A] px-3 py-2 flex items-center justify-between" data-testid="cc-exp2-sumber-grand">
        <span className="overline text-slate-300 text-[9px]">Total Seluruh Pengeluaran (Buku Bank + Petty Cash)</span>
        <span className="text-sm font-bold tabular-nums text-white" data-testid="cc-exp2-sumber-grand-total">{formatIDR(bankTotal + pcTotal)}</span>
      </div>
    </div>
  );
}

/* ============================== SUB-TAB 2: VERSI RAB ============================== */

// Bangun baris hierarki (prefix nomor seperti Rincian) + rollup nilai ke judul.
function buildRabRows(items) {
  const rows = [];
  const stack = [];
  for (const it of items.filter((i) => i.section === "pekerjaan")) {
    if (it.no) {
      const no = String(it.no);
      while (stack.length && !no.startsWith(stack[stack.length - 1].no + ".")) stack.pop();
      const entry = { type: "leader", key: it.id, no, subNo: "", desc: it.description, depth: stack.length ? stack[stack.length - 1].entry.depth + 1 : 0, rab: 0, teralokasi: 0 };
      rows.push(entry);
      stack.push({ no, entry });
    } else {
      const entry = { type: "item", key: it.id, no: "", subNo: it.sub_no, desc: it.description, depth: stack.length ? stack[stack.length - 1].entry.depth + 1 : 0, rab: it.nilai_rab, teralokasi: it.teralokasi, sisa: it.sisa, pct: it.pct, details: it.details || [] };
      rows.push(entry);
      for (const s of stack) { s.entry.rab += it.nilai_rab; s.entry.teralokasi += it.teralokasi; }
    }
  }
  return { rows, overhead: items.filter((i) => i.section === "overhead") };
}

const pctText = (p) => (p === null || p === undefined ? "—" : `${p}%`);
const indent = (depth, extra = 0) => ({ paddingLeft: `${((depth + extra) * 14) + 8}px` });

function VersiRabView({ pid }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(new Set()); // item yang di-expand

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await api.get(`/active-projects/${pid}/cost-control/pengeluaran/versi-rab`).then((r) => r.data);
      setData(d);
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat Versi RAB.");
    }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setOpen((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const groups = useMemo(() => (data?.groups || []).map((g) => ({ ...g, ...buildRabRows(g.items || []) })), [data]);
  const leafTotals = useMemo(() => {
    let rab = 0;
    for (const g of groups) for (const it of (g.items || [])) if (!it.no) rab += Number(it.nilai_rab || 0);
    return rab;
  }, [groups]);

  if (err) return <div className="p-4 text-sm text-rose-700" data-testid="cc-exp2-rab-error">{err}</div>;
  if (data === null) return <div className="p-6 text-sm text-slate-500" data-testid="cc-exp2-rab-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat Versi RAB…</div>;

  const grand = Number(data.grand_total || 0);

  const renderItemRow = (r) => {
    const expanded = open.has(r.key);
    const sisa = Math.round((r.rab - r.teralokasi) * 100) / 100;
    const pct = r.rab > 0 ? Math.round((r.teralokasi / r.rab) * 1000) / 10 : null;
    return (
      <Fragment key={r.key}>
        <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cc-exp2-rab-row-${r.key}`}>
          <td className="px-2 py-1.5 text-slate-400">—</td>
          <td className="px-2 py-1.5 text-slate-700">
            <span className="inline-flex items-center gap-0.5">
              {r.details.length > 0 ? (
                <button type="button" onClick={() => toggle(r.key)}
                  title={expanded ? "Sembunyikan transaksi pembentuk" : "Tampilkan transaksi pembentuk"}
                  data-testid={`cc-exp2-rab-toggle-${r.key}`}
                  className="p-0.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-200 rounded">
                  {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                </button>
              ) : <span className="inline-block w-[17px]" />}
              {r.subNo || "—"}
            </span>
          </td>
          <td className="px-2 py-1.5 text-slate-800 break-words" style={indent(r.depth)}>{r.desc}</td>
          <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{formatIDR(r.rab)}</td>
          <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-700" data-testid={`cc-exp2-rab-teralokasi-${r.key}`}>{formatIDR(r.teralokasi)}</td>
          <td className={`px-2 py-1.5 text-right tabular-nums ${sisa < -0.01 ? "text-rose-700 font-semibold" : "text-emerald-700"}`}>{formatIDR(sisa)}</td>
          <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{pctText(pct)}</td>
        </tr>
        {expanded && r.details.length > 0 && (
          <tr className="border-b border-slate-100" data-testid={`cc-exp2-rab-detail-${r.key}`}>
            <td colSpan={7} className="px-2 py-1 bg-slate-50/60">
              <table className="w-full text-[11px]" style={{ marginLeft: `${(r.depth + 1) * 14}px`, width: `calc(100% - ${(r.depth + 1) * 14}px)` }}>
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="px-2 py-1 overline text-slate-500 w-24">Tanggal</th>
                    <th className="px-2 py-1 overline text-slate-500 w-40">Sumber Kas</th>
                    <th className="px-2 py-1 overline text-slate-500 w-32">Periode / Referensi</th>
                    <th className="px-2 py-1 overline text-slate-500">Deskripsi</th>
                    <th className="px-2 py-1 overline text-slate-500 w-28 text-right">Nominal Alokasi</th>
                    <th className="px-2 py-1 overline text-slate-500 w-44">COA</th>
                  </tr>
                </thead>
                <tbody>
                  {r.details.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100" data-testid={`cc-exp2-rab-detail-row-${d.id}`}>
                      <td className="px-2 py-1 text-slate-600 whitespace-nowrap">{fmtDate(d.date)}</td>
                      <td className="px-2 py-1 text-slate-600">{d.source_label || (d.source_type === "petty_cash" ? "Petty Cash" : "Buku Bank")}</td>
                      <td className="px-2 py-1 text-slate-500 break-words">{d.period || d.ref || "—"}</td>
                      <td className="px-2 py-1 text-slate-700 break-words">{d.description || "—"}{d.note ? <span className="text-slate-400"> · {d.note}</span> : null}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-rose-600">{formatIDR(d.amount)}</td>
                      <td className="px-2 py-1 text-slate-600">{d.coa_code ? `${d.coa_code} — ${d.coa_name || ""}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const renderLeaderRow = (r) => {
    const sisa = Math.round((r.rab - r.teralokasi) * 100) / 100;
    const pct = r.rab > 0 ? Math.round((r.teralokasi / r.rab) * 1000) / 10 : null;
    return (
      <tr key={r.key} className="border-b border-slate-200 bg-slate-50/70" data-testid={`cc-exp2-rab-leader-${r.key}`}>
        <td className="px-2 py-1.5 font-bold text-slate-700">{r.no}</td>
        <td className="px-2 py-1.5 text-slate-400">—</td>
        <td className="px-2 py-1.5 font-bold text-[#0B1D3A] uppercase tracking-wide break-words" style={indent(r.depth)}>{r.desc}</td>
        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-700">{formatIDR(r.rab)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-700">{formatIDR(r.teralokasi)}</td>
        <td className={`px-2 py-1.5 text-right tabular-nums ${sisa < -0.01 ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{formatIDR(sisa)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{pctText(pct)}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-3" data-testid="cc-exp2-rab">
      <p className="text-[11px] text-slate-500" data-testid="cc-exp2-rab-hint">
        Pengeluaran yang <span className="font-semibold text-slate-700">sudah dialokasikan</span> via tab Klasifikasi Pengeluaran dikelompokkan ke item pekerjaan RAB — memakai <span className="font-semibold text-slate-700">nilai alokasi</span>, bukan seluruh nominal transaksi bila satu transaksi di-split ke beberapa item. Sisa Anggaran = Nilai RAB − Pengeluaran Teralokasi. Klik panah pada Sub No untuk melihat transaksi pembentuknya.
      </p>

      {Number(data.unclassified_total || 0) > 0.01 && (
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 flex items-center justify-between gap-3 flex-wrap" data-testid="cc-exp2-unclassified">
          <span>
            <b>{data.unclassified_count} transaksi</b> senilai <b className="tabular-nums">{formatIDR(data.unclassified_total)}</b> belum diklasifikasikan ke item RAB — tidak dimasukkan secara paksa ke perhitungan di bawah.
          </span>
          <span className="text-amber-700">Alokasikan melalui tab <b>Klasifikasi Pengeluaran</b>.</span>
        </div>
      )}

      {groups.map((g) => (
        <div key={g.project_id} className="border border-slate-200 overflow-x-auto bg-white" data-testid={`cc-exp2-rab-group-${g.project_id}`}>
          {g.label && (
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]" data-testid={`cc-exp2-rab-scope-${g.project_id}`}>
              {g.label}
            </div>
          )}
          <table className="w-full text-xs min-w-[960px]">
            <thead>
              <tr className="text-left bg-slate-50 border-b-2 border-slate-300">
                <th className="px-2 py-2 overline text-slate-700 w-14">No</th>
                <th className="px-2 py-2 overline text-slate-700 w-16">Sub No</th>
                <th className="px-2 py-2 overline text-slate-700">Item Pekerjaan</th>
                <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Nilai RAB</th>
                <th className="px-2 py-2 overline text-slate-700 w-32 text-right">Pengeluaran Teralokasi</th>
                <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Sisa Anggaran</th>
                <th className="px-2 py-2 overline text-slate-700 w-20 text-right">% Terpakai</th>
              </tr>
            </thead>
            <tbody data-testid={`cc-exp2-rab-tbody-${g.project_id}`}>
              {g.rows.map((r) => (r.type === "leader" ? renderLeaderRow(r) : renderItemRow(r)))}
              {g.overhead.length > 0 && (
                <Fragment>
                  <tr className="border-b border-slate-200 bg-amber-50/70" data-testid={`cc-exp2-rab-oh-head-${g.project_id}`}>
                    <td colSpan={7} className="px-2 py-1.5 font-bold text-[10px] uppercase tracking-wider text-amber-800">Overhead Cost</td>
                  </tr>
                  {g.overhead.map((it) => renderItemRow({
                    type: "item", key: it.id, no: "", subNo: it.sub_no, desc: it.description, depth: 0,
                    rab: it.nilai_rab, teralokasi: it.teralokasi, sisa: it.sisa, pct: it.pct, details: it.details || [],
                  }))}
                </Fragment>
              )}
              {g.rows.length === 0 && g.overhead.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[11px] text-slate-400 italic" data-testid={`cc-exp2-rab-empty-${g.project_id}`}>
                  Belum ada item RAB — tambahkan di tab Rincian Cost Control.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      <div className="border border-[#0B1D3A] bg-[#0B1D3A] px-3 py-2 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cc-exp2-rab-grand">
        <div>
          <div className="overline text-slate-300 text-[9px]">Total Nilai RAB</div>
          <div className="text-sm font-bold tabular-nums text-white" data-testid="cc-exp2-rab-grand-rab">{formatIDR(leafTotals)}</div>
        </div>
        <div>
          <div className="overline text-slate-300 text-[9px]">Total Pengeluaran Teralokasi</div>
          <div className="text-sm font-bold tabular-nums text-white" data-testid="cc-exp2-rab-grand-teralokasi">{formatIDR(grand)}</div>
        </div>
        <div>
          <div className="overline text-slate-300 text-[9px]">Sisa Anggaran</div>
          <div className="text-sm font-bold tabular-nums text-white" data-testid="cc-exp2-rab-grand-sisa">{formatIDR(Math.round((leafTotals - grand) * 100) / 100)}</div>
        </div>
        <div>
          <div className="overline text-slate-300 text-[9px]">% Terpakai</div>
          <div className="text-sm font-bold tabular-nums text-white" data-testid="cc-exp2-rab-grand-pct">{leafTotals > 0 ? pctText(Math.round((grand / leafTotals) * 1000) / 10) : "—"}</div>
        </div>
      </div>
    </div>
  );
}
